import React, { Suspense, lazy, useEffect, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";

import { GenerateDocumentRequest, courseDocumentsApi } from "api";
import { CourseDocumentOption, useCourse, useCourseDocumentStudents } from "api/hooks";
import {
  PaziaButton,
  PaziaInput,
  PaziaLoader,
  PaziaSelect,
} from "components/atoms";
import type { GenerationState } from "components/templates/GenerateCourseDocuments/GenerateDocumentsV2";

// The 2.0 generation screen pulls in pdfme (several MB), so it loads only when a 2.0 document is chosen.
const GenerateDocumentsV2 = lazy(() =>
  import("components/templates/GenerateCourseDocuments/GenerateDocumentsV2").then((module) => ({
    default: module.GenerateDocumentsV2,
  })),
);

interface CourseDocumentFormProps {
  studentsIds?: number[];
  onSubmit: () => void;
  courseDocumentOptions: CourseDocumentOption[];
  submitButtonRef?: React.RefObject<HTMLButtonElement>;
  /** Dokumenty 2.0 only: reports when documents are being generated, and whether any were. */
  onGenerationState?: (state: GenerationState) => void;
}
export const CourseDocumentForm = ({
  studentsIds,
  onSubmit,
  courseDocumentOptions,
  submitButtonRef,
  onGenerationState,
}: CourseDocumentFormProps) => {
  const { courseId } = useParams();
  const [templateId, setTemplateId] = React.useState("");
  const [generating, setGenerating] = React.useState(false);
  const selected = courseDocumentOptions.find((option) => option.value.toString() === templateId);

  return (
    <div>
      <TemplateForm
        courseDocumentOptions={courseDocumentOptions}
        onChange={(x) => setTemplateId(x.value.toString())}
        disabled={generating}
      />
      {templateId && selected?.version === 2 ? (
        selected.courseDocument?.v2 ? (
          <div className="mt-4">
            <Suspense fallback={<PaziaLoader position="center" />}>
              <GenerateDocumentsV2
                key={templateId}
                courseId={Number(courseId)}
                courseDocument={selected.courseDocument}
                v2={selected.courseDocument.v2}
                studentIds={studentsIds ?? []}
                submitButtonRef={submitButtonRef}
                onSubmit={onSubmit}
                onGenerationState={(state) => {
                  setGenerating(state.running);
                  onGenerationState?.(state);
                }}
              />
            </Suspense>
          </div>
        ) : (
          <div role="alert" className="mt-4 rounded border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800 dark:bg-rose-500/10 dark:border-rose-500/30 dark:text-rose-300">
            Nie można wczytać tego dokumentu 2.0.
          </div>
        )
      ) : (
        templateId && (
          <>
            <div className="flex justify-center py-4 font-semibold">Następujące dokumenty zostaną wygenerowane</div>
            <CourseDocumentPageTable
              templateId={Number(templateId)}
              submitButtonRef={submitButtonRef}
              courseId={Number(courseId)}
              onSubmit={onSubmit}
              studentsIds={studentsIds}
            />
          </>
        )
      )}
    </div>
  );
};

interface CourseDocumentPageTableProps {
  templateId: number;
  courseId: number;
  onSubmit: () => void;
  studentsIds?: number[];
  submitButtonRef?: React.RefObject<HTMLButtonElement>;
}

export const CourseDocumentPageTable = ({
  courseId,
  templateId,
  onSubmit,
  studentsIds = [],
  submitButtonRef,
}: CourseDocumentPageTableProps) => {
  const { data, loading } = useCourse(Number(courseId), false);

  const { data: courseDocument, loading: loadingDocument } = useCourseDocumentStudents(
    Number(courseId),
    Number(templateId),
    studentsIds,
  );
  if (loading || loadingDocument) return <PaziaLoader position="center" />;
  if (!data || !courseDocument) return null;

  const tableData = courseDocument.students
    // .filter((x) => studentsIds.includes(x.id))
    .map((student) => {
      let { placeholders, ...rest } = student;
      placeholders.forEach((placeholder) => {
        // @ts-ignore
        rest[placeholder.documentPlaceholderId] = placeholder.placeholderValue;
      });
      return { ...rest };
    });

  const placeholders = courseDocument.documentTemplate.pages.flatMap((page) => page.placeholders);

  return (
    <div>
      <FormCards
        tableData={tableData}
        placeholders={placeholders}
        onSubmit={onSubmit}
        courseId={Number(courseId)}
        templateId={Number(templateId)}
        submitButtonRef={submitButtonRef}
        documentName={courseDocument.documentTemplate.name}
        courseName={data.data.name}
      />
    </div>
  );
};

interface TemplateFormProps {
  courseDocumentOptions: CourseDocumentOption[];
  onChange: (opt: CourseDocumentOption) => void;
  disabled?: boolean;
}
export const TemplateForm = ({ courseDocumentOptions, onChange, disabled }: TemplateFormProps) => {
  const methods = useForm({
    defaultValues: {},
  });
  return (
    <FormProvider {...methods}>
      <form>
        <PaziaSelect
          label="Wybierz dokument"
          name="template"
          options={courseDocumentOptions}
          onChange={onChange}
          disabled={disabled}
        />
      </form>
    </FormProvider>
  );
};
interface FormCardsProps {
  tableData: any[]; // TODO any
  placeholders: any[]; // TODO any
  courseId: number;
  documentName: string;
  courseName: string;
  templateId: number;
  onSubmit: () => void;
  submitButtonRef?: React.RefObject<HTMLButtonElement>;
}

const FormCards = ({ tableData, placeholders, onSubmit, templateId, submitButtonRef, courseId }: FormCardsProps) => {
  const tableDataRef = useRef(tableData);
  const [stableTableData, setStableTableData] = React.useState(tableData);

  // Only update stableTableData when tableData actually changes (deep comparison)
  useEffect(() => {
    const hasChanged = JSON.stringify(tableDataRef.current) !== JSON.stringify(tableData);
    if (hasChanged) {
      tableDataRef.current = tableData;
      setStableTableData(tableData);
    }
  }, [tableData]);

  const [expandedCards, setExpandedCards] = React.useState<Set<number>>(
    new Set(stableTableData.map((student) => student.id)),
  );

  const methods = useForm({
    defaultValues: {
      students: stableTableData,
    },
  });

  useEffect(() => {
    methods.reset({ students: stableTableData });
  }, [stableTableData]);

  const { handleSubmit } = methods;

  const toggleCard = (studentId: number) => {
    setExpandedCards((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(studentId)) {
        newSet.delete(studentId);
      } else {
        newSet.add(studentId);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    setExpandedCards(new Set(stableTableData.map((student) => student.id)));
  };

  const collapseAll = () => {
    setExpandedCards(new Set());
  };

  const onFormSubmit = async (data: any) => {
    const request: GenerateDocumentRequest = {
      // @ts-ignore
      students: data.students.map(({ id, firstName, surname, email, ...student }) => ({
        studentId: id,
        email,
        firstName,
        surname,
        placeholders: Object.keys(student).map((key) => ({
          documentPlaceholderId: Number(key),
          placeholderValue: student[key],
        })),
      })),
    };
    await courseDocumentsApi.generateDocument(Number(courseId), Number(templateId), request);
    onSubmit();
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onFormSubmit)}>
        {/* Expand/Collapse All Controls */}
        <div className="mb-6 flex items-center justify-between rounded-lg bg-slate-50 p-4 dark:bg-slate-800">
          <div className="text-sm font-medium text-slate-700 dark:text-slate-300">
            Zarządzaj kartami studentów ({stableTableData.length} studentów)
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={expandAll}
              className="rounded-md bg-blue-100 px-3 py-1 text-xs font-medium text-blue-700 hover:bg-blue-200 dark:bg-blue-900 dark:text-blue-300 dark:hover:bg-blue-800"
            >
              Rozwiń wszystkie
            </button>
            <button
              type="button"
              onClick={collapseAll}
              className="rounded-md bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600"
            >
              Zwiń wszystkie
            </button>
          </div>
        </div>

        <div className="space-y-4">
          {stableTableData.map((student, index) => {
            const isExpanded = expandedCards.has(student.id);
            return (
              <div
                key={student.id}
                className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm transition-all duration-200 dark:border-slate-700 dark:bg-slate-800"
              >
                {/* Student Header - Always Visible */}
                <button
                  type="button"
                  onClick={() => toggleCard(student.id)}
                  className="flex w-full items-center justify-between p-4 text-left hover:bg-slate-50 dark:hover:bg-slate-700"
                >
                  <div className="flex items-center gap-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-400">
                      <span className="text-sm font-semibold">
                        {student.firstName.charAt(0)}
                        {student.surname.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 dark:text-slate-100">
                        {student.firstName} {student.surname}
                      </h3>
                      <p className="text-xs text-slate-600 dark:text-slate-400">ID: {student.id}</p>
                    </div>
                  </div>

                  {/* Expand/Collapse Icon */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500 dark:text-slate-400">{isExpanded ? "Zwiń" : "Rozwiń"}</span>
                    <svg
                      className={`h-5 w-5 text-slate-400 transition-transform ${isExpanded ? "rotate-180" : ""}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </button>

                {/* Collapsible Content */}
                {isExpanded && (
                  <div className="border-t border-slate-200 p-4 dark:border-slate-700">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      {placeholders.map((placeholder) => (
                        <div key={placeholder.id} className="space-y-2">
                          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                            {placeholder.name}
                          </label>
                          <PaziaInput name={`students[${index}].${placeholder.id}`} className="w-full" />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <PaziaButton title="Submit" type="success" className="hidden" buttonRef={submitButtonRef} />
      </form>
    </FormProvider>
  );
};
